import { useState } from 'react';
import {
  Button,
  Modal,
  Dropdown,
  Form,
  Row,
  Col,
  Accordion,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Formik, ErrorMessage } from 'formik';
import Tippy from '@tippyjs/react';
import Select from 'react-select';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { AccordionToggleWithButtonForRole } from '../common';
import {
  activateLoader,
  deactivateLoader,
  updateMemberData,
} from '../../actions/team';
import { roleAndPermissionsService, teamService } from '../../services';
import { CUSTOM_ROLE } from '../../constants';

const MemberRoleModal = ({ userId, typeId, typeName }) => {
  const [toggleArrowChange, arrowChange] = useState(false);
  const arrowChangeToggle = () => {
    arrowChange(!toggleArrowChange);
  };
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [noteEnable, setNoteEnable] = useState(true);
  const [rolesAll, setRolesAll] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [selectedDefaultPermissionAll, setSelectedDefaultPermissionAll] =
    useState([]);
  const [selectedDefaultPermission, setSelectedDefaultPermission] =
    useState(null);
  const [selectedExtraPermission, setSelectedExtraPermission] = useState([]);
  const [userTypeId, setUserTypeId] = useState('');
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleGetDefaultPermission = (userTypeIds) => {
    dispatch(activateLoader());
    roleAndPermissionsService
      .get_default_permissions()
      .then((res) => {
        if (res && res.data && res.data.length) {
          const additionalPermissionsList = res.data;
          const permissionsListFilter = additionalPermissionsList.filter(
            (item) => {
              item.permissions = item.permissions.filter((it) => it.isDefault);
              return item;
            }
          );

          const permissionUiBindFormat = permissionsListFilter.map((item) => {
            const permissionList = item.permissions;

            const groupByModuleName = Object.groupBy(
              permissionList,
              ({ moduleName }) => moduleName
            );
            const groupByObjectKey = Object.keys(groupByModuleName).map(
              (itemname) => ({
                name: itemname,
                list: groupByModuleName[itemname],
              })
            );

            return {
              typeName: item.userTypes.typeName,
              typeId: item.userTypes.typeId,
              permissions: groupByObjectKey,
            };
          });

          setSelectedDefaultPermissionAll(permissionUiBindFormat);

          const selectDefaultIndex = permissionUiBindFormat.findIndex(
            (item) => item.typeId === userTypeIds
          );
          if (selectDefaultIndex > -1) {
            setSelectedDefaultPermission(
              permissionUiBindFormat[selectDefaultIndex]
            );
          } else {
            toast.error(CUSTOM_ROLE.DEFAULT_PERMISSION_ERROR);
          }
        } else {
          toast.error(CUSTOM_ROLE.DEFAULT_PERMISSION_ERROR);
        }
        dispatch(deactivateLoader());
      })
      .catch((errResponse) => {
        dispatch(deactivateLoader());
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        }
      });
  };

  const handleGetRoles = () => {
    setSelectedExtraPermission([]);
    setSelectedDefaultPermission(null);
    dispatch(activateLoader());
    roleAndPermissionsService
      .get_roles()
      .then((response) => {
        roleAndPermissionsService
          .get_user_types()
          .then((userTypesResponse) => {
            const userTypesResponseData =
              (userTypesResponse && userTypesResponse.data) || [];
            const resData = userTypesResponseData.map((item) => {
              item.userType = item.label;
              item.userTypeId = item.value;
              return item;
            });

            const userTypesData = resData
              .map((item) => ({
                label: item.userType,
                value: item.userTypeId,
              }))
              .filter(
                (value, index, self) =>
                  index === self.findIndex((t) => t.value === value.value)
              );

            setUserTypes(
              resData
                .map((item) => {
                  const serialNumber =
                    userTypesResponseData
                      .map((items) => items.label)
                      .indexOf(item.userType) + 1;
                  return {
                    label: item.userType,
                    value: item.userTypeId,
                    serialNumber,
                  };
                })
                .filter(
                  (value, index, self) =>
                    index === self.findIndex((t) => t.value === value.value)
                )
                .sort((a, b) => a.serialNumber - b.serialNumber)
            );
            const currentIndex = userTypesData.findIndex(
              (item) => item.value === typeId
            );

            if (currentIndex > -1) {
              const userTypeIdSingle = userTypesData[currentIndex].value;
              setUserTypeId(userTypeIdSingle);
              handleGetDefaultPermission(userTypeIdSingle);
              if (response && response.data && response.data.length) {
                const responseData = response.data;
                setRolesAll(responseData);

                setRoles(
                  responseData
                    .filter((item) => item.userType === typeName)
                    .map((item) => ({
                      value: item._id,
                      label: item.roleName,
                    }))
                );
                setNoteEnable(true);
              }
              handleShow();
            }

            dispatch(deactivateLoader());
          })
          .catch((errResponse) => {
            dispatch(deactivateLoader());
            if ('message' in errResponse) {
              toast.error(errResponse.message);
            }
          });
      })
      .catch((errResponse) => {
        dispatch(deactivateLoader());
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        }
      });
  };

  const handleAssignRole = (values) => {
    dispatch(activateLoader());
    const params = {
      customRoleId: values.role,
      userTypeId: values.userType,
      teamMemberId: userId,
      teamOwnerId: localStorage.getItem('teamOwnerId'),
    };
    roleAndPermissionsService
      .update_member_role(params)
      .then((res) => {
        if (res && res.status) {
          teamService
            .get_team_members_data({
              teamOwnerId: localStorage.getItem('teamOwnerId'),
              summaryList: false,
              filter: JSON.stringify({
                memberId: userId,
              }),
            })
            .then((responses) => {
              if (responses.data) {
                const { members } = responses.data;

                dispatch(updateMemberData(members[0]));

                toast.success(CUSTOM_ROLE.UPDATE_MEMBER_ROLE);
                handleClose();
              }
            });
        }
        dispatch(deactivateLoader());
      })
      .catch((errResponse) => {
        dispatch(deactivateLoader());
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        }
      });
  };

  const handleChangeUserType = (userType) => {
    setRoles([]);

    setNoteEnable(false);
    setTimeout(() => {
      setSelectedExtraPermission([]);
      const rolesAllow = rolesAll;
      setUserTypeId(userType);

      setRoles(
        rolesAllow
          .filter((item) => item.userTypeId === userType)
          .map((item) => ({
            value: item._id,
            label: item.roleName,
          }))
      );

      const selectDefaultIndex = selectedDefaultPermissionAll.findIndex(
        (item) => item.typeId === userType
      );
      if (selectDefaultIndex > -1) {
        setSelectedDefaultPermission(
          selectedDefaultPermissionAll[selectDefaultIndex]
        );
      } else {
        toast.error(CUSTOM_ROLE.DEFAULT_PERMISSION_ERROR);
      }
      setNoteEnable(true);
    }, 100);
  };

  const handleGetExtraPermission = (params) => {
    const newParam = {
      roleId: params.value,
      userTypeId,
    };
    dispatch(activateLoader());
    setSelectedExtraPermission([]);
    roleAndPermissionsService
      .get_role(newParam)
      .then((res) => {
        if (res && res.data && res.data.length) {
          const permissionsListsData = res.data;
          const permissionListSelected = permissionsListsData.filter(
            (item) => item.isSelected
          );

          const groupByModuleName = Object.groupBy(
            permissionListSelected,
            ({ moduleName }) => moduleName
          );
          const groupByObjectKeyPermissionList = Object.keys(
            groupByModuleName
          ).map((itemname) => ({
            name: itemname,
            list: groupByModuleName[itemname],
          }));

          setSelectedExtraPermission(groupByObjectKeyPermissionList);
        } else {
          toast.error(CUSTOM_ROLE.EXTRA_PERMISSION_ERROR);
        }
        dispatch(deactivateLoader());
      })
      .catch((errResponse) => {
        dispatch(deactivateLoader());
        if ('message' in errResponse) {
          toast.error(errResponse.message);
        }
      });
  };

  return (
    <>
      <Dropdown.Item onClick={handleGetRoles}>
        Assign Role/User Type
      </Dropdown.Item>
      <Modal show={show} onHide={handleClose} animation={false} centered>
        <Modal.Header>
          <Modal.Title className="m-0"> Assign Role </Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>

        <Formik
          enableReinitialize
          initialValues={{
            userType: userTypeId,
            role: '',
          }}
          validationSchema={Yup.object({
            userType: Yup.string().required('Please select user type.'),
          })}
          onSubmit={(values) => {
            handleAssignRole(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Modal.Body>
                <section>
                  <Form.Group controlId="type">
                    <Form.Label className="w-100">
                      <div className="float-start">
                        User Type
                        <Tippy
                          content={`${'Default type to inherit permission from'}`}
                          theme="light"
                          key="2"
                        >
                          <span className="p-2 d-inline-block">
                            <i className="mdi mdi-information-outline" />
                          </span>
                        </Tippy>
                      </div>
                      <div className="float-end">
                        <ul className="nav">
                          <li className="nav-item ">
                            <Link
                              to="/help/permissions"
                              className="nav-link nav-underline text-decoration-underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              User type permissions
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </Form.Label>
                    <Select
                      defaultValue={{
                        label: typeName,
                        value: typeId,
                      }}
                      name="userType"
                      options={userTypes}
                      onChange={(e) => {
                        formikprops.setFieldValue('userType', e.value);
                        handleChangeUserType(e.value);
                      }}
                      className="text-capitalize"
                    />
                    <ErrorMessage
                      name="userType"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </Form.Group>
                  {roles.length === 0 && noteEnable && (
                    <div className="mb-4 card p-3">
                      <p className="mb-0">
                        <b>Note : </b>No roles created against above user type.
                        Please create a new custom role for this user or change
                        the user type to get other created roles.
                      </p>
                    </div>
                  )}
                  {roles.length > 0 && (
                    <Form.Group controlId="type">
                      <Form.Label>Role</Form.Label>

                      <Select
                        name="role"
                        options={roles}
                        onChange={(e) => {
                          formikprops.setFieldValue('role', e.value);
                          handleGetExtraPermission(e);
                        }}
                      />

                      <ErrorMessage
                        name="role"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  )}
                  {selectedDefaultPermission &&
                    selectedDefaultPermission.permissions &&
                    selectedDefaultPermission.permissions.length > 0 && (
                      <h4 className="mb-0 text-muted">Default Permissions</h4>
                    )}
                  {selectedDefaultPermission &&
                    selectedDefaultPermission.permissions &&
                    selectedDefaultPermission.permissions.length > 0 && (
                      <div
                        className={`overflow-auto mt-3 ${
                          selectedExtraPermission &&
                          selectedExtraPermission.length > 0
                            ? 'mb-4'
                            : 'mb-0'
                        }`}
                        style={{ maxHeight: '200px' }}
                      >
                        <div className="card mb-0">
                          <div className="card-body p-0">
                            <ul className="list-group list-group-flush pe-0">
                              <Accordion
                                defaultActiveKey="0"
                                className="accordion-flush"
                              >
                                {selectedDefaultPermission &&
                                  selectedDefaultPermission.permissions &&
                                  selectedDefaultPermission.permissions.length >
                                    0 &&
                                  selectedDefaultPermission.permissions.map(
                                    (permission, index) => (
                                      <li
                                        className="list-group-item"
                                        key={index}
                                      >
                                        <Row>
                                          <Col className="px-2">
                                            <div className="d-flex justify-content-start text-capitalize">
                                              <b>{permission.name}</b>
                                            </div>
                                          </Col>
                                          <Col sm lg="1">
                                            <div className="d-flex justify-content-end">
                                              <AccordionToggleWithButtonForRole
                                                eventKey={index + 1}
                                                callback={arrowChangeToggle}
                                              />
                                            </div>
                                          </Col>
                                        </Row>

                                        <Accordion.Collapse
                                          eventKey={index + 1}
                                        >
                                          <div className="p-2">
                                            {permission.list &&
                                              permission.list.length > 0 &&
                                              permission.list.map((item, i) => (
                                                <div className="mt-1" key={i}>
                                                  {item.permissionNameUI
                                                    .toLowerCase()[0]
                                                    .toUpperCase() +
                                                    item.permissionNameUI
                                                      .toLowerCase()
                                                      .substring(1)}
                                                </div>
                                              ))}
                                          </div>
                                        </Accordion.Collapse>
                                      </li>
                                    )
                                  )}
                              </Accordion>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  {selectedExtraPermission &&
                    selectedExtraPermission.length > 0 && (
                      <h4 className="mb-0 text-muted">Role Permissions</h4>
                    )}
                  {selectedExtraPermission &&
                    selectedExtraPermission.length > 0 && (
                      <div
                        className="overflow-auto mb-0 mt-3"
                        style={{ maxHeight: '200px' }}
                      >
                        <div className="card mb-0">
                          <div className="card-body p-0">
                            <ul className="list-group list-group-flush pe-0">
                              <Accordion
                                defaultActiveKey="0"
                                className="accordion-flush"
                              >
                                {selectedExtraPermission &&
                                  selectedExtraPermission.length > 0 &&
                                  selectedExtraPermission.map(
                                    (permission, index) => (
                                      <li
                                        className="list-group-item"
                                        key={index}
                                      >
                                        <Row>
                                          <Col className="px-2">
                                            <div className="d-flex justify-content-start text-capitalize">
                                              <b>{permission.name}</b>
                                            </div>
                                          </Col>
                                          <Col sm lg="1">
                                            <div className="d-flex justify-content-end">
                                              <AccordionToggleWithButtonForRole
                                                eventKey={index + 1}
                                                callback={arrowChangeToggle}
                                              />
                                            </div>
                                          </Col>
                                        </Row>

                                        <Accordion.Collapse
                                          eventKey={index + 1}
                                        >
                                          <div className="p-2">
                                            {permission.list &&
                                              permission.list.length > 0 &&
                                              permission.list.map((item, i) => (
                                                <div className="mt-1" key={i}>
                                                  {item.permissionNameUI
                                                    .toLowerCase()[0]
                                                    .toUpperCase() +
                                                    item.permissionNameUI
                                                      .toLowerCase()
                                                      .substring(1)}
                                                </div>
                                              ))}
                                          </div>
                                        </Accordion.Collapse>
                                      </li>
                                    )
                                  )}
                              </Accordion>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                </section>
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-between">
                <Button
                  variant="link"
                  onClick={() => {
                    handleClose(false);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Submit
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};
MemberRoleModal.propTypes = {
  userId: PropTypes.string,
  typeId: PropTypes.string,
  typeName: PropTypes.string,
};
export default MemberRoleModal;
